import { DataTable } from "./DataTable/index.tsx";
import { getCols } from "../../common/getCols.ts";
import { helpers } from "../util/helpers.ts";
import { toWorker } from "../util/toWorker.ts";
import { useLocal } from "../util/local.ts";
import { wrappedPlayerNameLabels } from "./PlayerNameLabels.tsx";
import type { View } from "../../common/types.ts";

// Negotiations tab of the Free Agents page: offers from the user's team still waiting for an answer
export const PendingOffers = ({
	numRosterSpots,
	onNegotiate,
	pendingOffers,
}: {
	numRosterSpots: number;
	onNegotiate: (pid: number) => Promise<void>;
	pendingOffers: View<"freeAgents">["pendingOffers"];
}) => {
	const { challengeNoRatings, gameSimInProgress } = useLocal([
		"challengeNoRatings",
		"gameSimInProgress",
	]);

	// Columns not in the shared list are passed as full definitions
	const cols = getCols(
		[
			"Name",
			"Pos",
			"Age",
			"Ovr",
			"Pot",
			{
				title: "Your Offer",
				desc: "Annual salary and last year of the contract",
				sortSequence: ["desc", "asc"],
				sortType: "number",
			},
			{
				title: "His Counter",
				desc: "What it takes today to beat the other offers, for your contract length",
				sortSequence: ["desc", "asc"],
				sortType: "number",
			},
			{
				title: "Decides In",
				desc: "Days until the player picks a team",
				sortSequence: ["asc", "desc"],
				sortType: "number",
			},
			{
				title: "Other Offers",
				desc: "Best offers from other teams right now",
				sortSequence: ["desc", "asc"],
				sortType: "number",
			},
			"Actions",
		],
		{
			Actions: { sortSequence: [], noSearch: true, width: "1px" },
		},
	);

	const rows = pendingOffers.map((p) => ({
		key: p.pid,
		data: [
			wrappedPlayerNameLabels({
				pid: p.pid,
				injury: p.injury,
				jerseyNumber: p.jerseyNumber,
				skills: p.ratings.skills,
				defaultWatch: p.watch,
				firstName: p.firstName,
				firstNameShort: p.firstNameShort,
				lastName: p.lastName,
			}),
			p.ratings.pos,
			p.age,
			!challengeNoRatings ? p.ratings.ovr : null,
			!challengeNoRatings ? p.ratings.pot : null,
			{
				value: `${helpers.formatCurrency(p.offer.amount / 1000, "M")} thru ${p.offer.exp}`,
				sortValue: p.offer.amount,
			},
			{
				value:
					p.counter === undefined
						? null
						: p.counter <= p.offer.amount
							? "You lead"
							: `${helpers.formatCurrency(p.counter / 1000, "M")}`,
				sortValue: p.counter ?? 0,
			},
			{
				value: p.offer.daysLeft === 1 ? "1 day" : `${p.offer.daysLeft} days`,
				sortValue: p.offer.daysLeft,
			},
			{
				value:
					p.bids.length > 0
						? p.bids
								.map(
									(bid: { abbrev: string; amount: number; exp: number }) =>
										`${bid.abbrev} ${helpers.formatCurrency(bid.amount / 1000, "M")} thru ${bid.exp}`,
								)
								.join(", ")
						: "None",
				sortValue: p.bids[0]?.amount ?? 0,
			},
			{
				value: (
					<>
						<button
							className="btn btn-light-bordered btn-xs"
							disabled={gameSimInProgress}
							onClick={async () => {
								await onNegotiate(p.pid);
							}}
						>
							Change offer
						</button>
						<button
							className="btn btn-light-bordered btn-xs ms-1"
							disabled={gameSimInProgress}
							onClick={async () => {
								await toWorker("main", "withdrawFreeAgencyOffer", p.pid);
							}}
						>
							Withdraw
						</button>
					</>
				),
				classNames: "text-nowrap",
			},
		],
	}));

	return (
		<>
			<p>
				A free agent with no other offers signs right away. One with other
				offers counters every side: he tells you what it takes, asks the other
				teams to beat you, and decides in a few days. Then he signs with the
				best offer, weighing money, contract length and his own preferences.
				Changing your offer restarts the clock.
			</p>
			{numRosterSpots <= 0 ? (
				<p className="text-warning">
					Your roster is full. You can still make offers, but if a player signs
					you'll need to open a spot before the next game.
				</p>
			) : null}
			{rows.length > 0 ? (
				<DataTable
					cols={cols}
					defaultSort={[7, "asc"]}
					name="FreeAgents:Negotiations"
					rows={rows}
				/>
			) : (
				<p className="text-body-secondary">
					No negotiations in progress. Make an offer from the Free Agents tab.
				</p>
			)}
		</>
	);
};
